import React, { useState, Component } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';
import { MyceliumNetworkHero } from '../components/MyceliumNetworkHero';
import { DNAHelixHero } from '../components/DNAHelixHero';
interface TeamMember {
  name: string;
  role: string;
  story: string;
  background: string;
  image: string;
}
interface AdvisorMember {
  name: string;
  role: string;
  bio: string;
  image?: string;
}
interface CommitteeMember {
  name: string;
  bio: string;
  image?: string;
}
export function TeamSplitScreen() {
  const [selectedMember, setSelectedMember] = useState<{
    type: 'core' | 'advisor' | 'committee';
    index: number;
  } | null>(null);
  const {
    scrollYProgress
  } = useScroll();
  const coreTeam: TeamMember[] = [{
    name: 'Natalya Wallin',
    role: 'Founder & Partnership Builder',
    story: "Natalya is a systems thinker who has spent her career at the intersection of science, policy, social impact, and global capital. Her background spans biology, public policy, and business which gives her a unique lens with which to see the evolving biomimicry opportunity and build something new around it. Before founding Vitality, she helped build and launch a global fund to address forced labor risks in supply chains. She served as managing director, overseeing deployment of $75 million in public and private funding across nine countries. Her previous experience engaging the White House, State Department, corporations, and civil society organizations informs the cross-sector fluency that now drives Vitality's thesis and partnerships.",
    background: 'She is also an ultrarunner and 8th place female finisher at Moab 240 which signals how she operates: long-horizon, high-tolerance for the unknown, built for endurance. Natalya holds degrees in Biology and Public Policy, as well as an Executive MBA from Duke University.',
    image: 'https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg'
  }, {
    name: 'Patrick Livingston',
    role: 'Built Environment Expert & Portfolio Construction',
    story: 'Patrick is an engineer and infrastructure builder who has spent 12 years bringing complex, large-scale projects from concept to completion. With dual degrees in Civil and Biological Engineering and an MBA from Duke, he sits at exactly the intersection this work requires: He has a deep understanding of how things are physically built and what it takes to finance them. He has delivered over $1 billion in constructed infrastructure, navigating the technical, regulatory, and financial layers that stop most projects before they start.',
    background: 'Patrick brings to Vitality what is often missing in early-stage investment: A rigorous, grounded understanding of what it actually takes to build in the physical world and an understanding of what patient, well-structured capital makes possible.',
    image: 'https://cdn.magicpatterns.com/uploads/oagLnQyesCH4gCMgV853L2/Pat_Headshot.jpg'
  }, {
    name: 'Rollin Nash',
    role: 'Operations & Resource Planning Specialist',
    story: 'Rollin is a builder who converts big strategic visions to tactics and implementation. A military veteran and systems architect from an Ohio farming community, he has spent 12 years planning and implementing high-stakes, multi-million dollar initiatives including first-in-human clinical trials, proof-of-concept military operations around the world, and complex organizational transformations. He knows exactly what it takes to build rigorous processes under real world pressures and constraints in local environments and global contexts. He brings that operational discipline to everything Vitality does.',
    background: 'His roots in a farming community and military experience give him a grounding in the physical and operational realities of delivering and documenting processes and results. Rollin earned his Bachelor’s from West Point while studying Kinesiology and Mechanical Engineering and earned his MBA from Duke.',
    image: 'https://cdn.magicpatterns.com/uploads/aQWr6mTKqjSUTCytDF8GyV/Headshot.jpg'
  }];
  const advisors: AdvisorMember[] = [{
    name: 'Melissa Kim',
    role: 'Advisor & Original Co-founder',
    bio: 'Experienced executive, entrepreneur, and Vitality co-founder who advises startups on driving product innovation, growth, and peak performance.',
    image: 'https://cdn.magicpatterns.com/uploads/7TaUSrmKqXQKcWYT8kcifC/Melissa_Headshot.jpg'
  }, {
    name: 'David Balkcom',
    role: 'Advisor, Finance & Agriculture',
    bio: 'Managing Director at FTI advising startups on turnaround, transformation, and due diligence, with deep experience in controlled environment agriculture and natural resources.',
    image: 'https://cdn.magicpatterns.com/uploads/8EqyD625fqmGoK7SfdyZho/David_Headshot.jpg'
  }, {
    name: 'Dr. Jean Baderschneider',
    role: 'Advisor, Global Engagement & Climate Finance',
    bio: 'Global supply chain and climate finance leader with 35 years of international experience, former VP of Global Procurement at ExxonMobil and non-executive Director of Fortescue Metals Group.',
    image: 'https://cdn.magicpatterns.com/uploads/oZoevN8jFFQA4NHrwEzrwg/Jean_Bio_pic_2026.png'
  }, {
    name: 'Matt Esposito',
    role: 'Advisor, Investor Relations',
    bio: 'Strategic advisor with deep expertise in venture capital and startup ecosystems.',
    image: 'https://cdn.magicpatterns.com/uploads/hYDVwGv8avLAL5fqGmTYmq/Matt_Headshot.jpg'
  }];
  const committee: CommitteeMember[] = [{
    name: 'Jordan McAlister',
    bio: 'Head of Investment & Innovation Strategy with Hyperspace Ventures',
    image: 'https://cdn.magicpatterns.com/uploads/sUj2TqUKbbrXFrhPY4ndmT/Jordan_Headshot.jpg'
  }, {
    name: 'Kate Knight',
    bio: 'Executive scaling coach and advisor with 20 years of experience',
    image: 'https://cdn.magicpatterns.com/uploads/ua9ycBvhPbjjrzg8hWCre9/Kate_Headshot.jpg'
  }, {
    name: 'Robin Berry',
    bio: 'Director of Lab to Market Innovation at ASU Biomimicry Center',
    image: 'https://cdn.magicpatterns.com/uploads/srnieMTZyCgwgg24unbedV/Robin_Headshot.jpg'
  }, {
    name: 'Olivia Hanrahan-Soar',
    bio: 'Expert in innovative finance and impact investing',
    image: 'https://cdn.magicpatterns.com/uploads/3V4HKj9gszSL8YVnqQCohF/8_Olivia.jpg'
  }, {
    name: 'Medina Korda Poole',
    bio: 'High-impact leader managing $150M+ portfolios globally',
    image: 'https://cdn.magicpatterns.com/uploads/ir1uoqUiVwJ7RcLbkkq7CA/6_Medina.png'
  }, {
    name: 'Chris Burton',
    bio: 'Experienced investor and operator with a focus on sustainable technologies.',
    image: 'https://cdn.magicpatterns.com/uploads/pRjZWzihcQPZV74t433sRU/5_Chris.jpg'
  }];
  return <div className="min-h-screen bg-black" data-id="element-2082">
      {/* Progress Indicator */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-vitalityTeal z-50 origin-left" style={{
      scaleX: scrollYProgress
    }} data-id="element-2083" />

      {/* Hero Intro */}
      <section className="h-screen flex items-center justify-center relative overflow-hidden" data-id="element-2084">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e2533] to-black" data-id="element-2085" />

        {/* Background image - mycelium network microscope */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-15" data-id="element-2086" />

        {/* Emphasized SVG overlay */}
        <div className="absolute inset-0 opacity-60" data-id="element-2087">
          <MyceliumNetworkHero data-id="element-2088" />
        </div>

        <div className="relative z-10 text-center px-4" data-id="element-2089">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-6xl md:text-8xl font-bold text-white mb-6" data-id="element-2090">
            Our Team
          </motion.h1>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="flex justify-center" data-id="element-2091">
            <ChevronDown className="w-8 h-8 text-vitalityTeal animate-bounce" data-id="element-2092" />
          </motion.div>
        </div>
      </section>

      {/* Core Team - Full Viewport Sections */}
      {coreTeam.map((member, index) => <SplitScreenSection key={member.name} member={member} index={index} imageOnLeft={index % 2 === 0} onReadMore={() => setSelectedMember({
      type: 'core',
      index
    })} data-id="element-2093" />)}

      {/* Advisors - Compact Section */}
      <section className="min-h-screen flex items-center bg-gradient-to-b from-black to-[#0e2533] py-20" data-id="element-2094">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-2095">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-16" data-id="element-2096">
            <h2 className="text-5xl font-bold text-white mb-4" data-id="element-2097">Advisors</h2>
            <p className="text-gray-400 text-xl" data-id="element-2098">
              Strategic guidance from industry veterans
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-2099">
            {advisors.map((advisor, index) => <motion.div key={advisor.name} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-2100">
                <div className="flex items-start gap-4 sm:gap-6" data-id="element-2101">
                  {advisor.image ? <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex-shrink-0 overflow-hidden border-2 border-[#a0e6e9]/30" data-id="element-2102">
                      <img src={advisor.image} alt={`${advisor.name} headshot`} className="w-full h-full object-cover" data-id="element-2103" />
                    </div> : <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#a0e6e9]/20 to-[#a0e6e9]/5 flex items-center justify-center flex-shrink-0 border-2 border-[#a0e6e9]/30" data-id="element-2104">
                      <span className="text-2xl sm:text-3xl font-bold text-[#a0e6e9]/70" data-id="element-2105">
                        {advisor.name[0]}
                      </span>
                    </div>}
                  <div data-id="element-2106">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" data-id="element-2107">
                      {advisor.name}
                    </h3>
                    <p className="text-[#a0e6e9] text-sm font-medium mb-3" data-id="element-2108">
                      {advisor.role}
                    </p>
                    <p className="text-gray-400 text-sm sm:text-base" data-id="element-2109">
                      {advisor.bio}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Committee - Simple List */}
      <section className="py-20 bg-[#0e2533]" data-id="element-2110">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2111">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-12" data-id="element-2112">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-2113">
              Investment Review Committee
            </h2>
            <p className="text-gray-500 text-lg mb-2" data-id="element-2114">
              Expert evaluation and due diligence
            </p>
            <p className="text-gray-400 text-sm italic" data-id="element-2115">
              These experts contribute their insights on a voluntary basis in
              support of the mission
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2116">
            {committee.map((member, index) => <motion.div key={member.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="p-6 rounded-xl bg-white/5 border border-white/10" data-id="element-2117">
                <div className="flex items-start gap-4" data-id="element-2118">
                  {member.image ? <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden border-2 border-white/10" data-id="element-2119">
                      <img src={member.image} alt={`${member.name} headshot`} className="w-full h-full object-cover" data-id="element-2120" />
                    </div> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center flex-shrink-0 border-2 border-white/10" data-id="element-2121">
                      <span className="text-lg font-bold text-white/50" data-id="element-2122">
                        {member.name[0]}
                      </span>
                    </div>}
                  <div data-id="element-2123">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-2124">
                      {member.name}
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-2125">{member.bio}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence data-id="element-2126">
        {selectedMember !== null && <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelectedMember(null)} data-id="element-2127">
            <motion.div className="bg-[#1c3f4d] rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto border-2 border-vitalityTeal/30 relative" initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} data-id="element-2128">
              <button onClick={() => setSelectedMember(null)} className="absolute top-6 right-6 text-white hover:text-vitalityTeal transition-colors" data-id="element-2129">
                <X className="w-6 h-6" data-id="element-2130" />
              </button>
              <div className="p-10" data-id="element-2131">
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-2" data-id="element-2132">
                  {coreTeam[selectedMember.index].role}
                </p>
                <h3 className="text-4xl font-bold text-white mb-8" data-id="element-2133">
                  {coreTeam[selectedMember.index].name}
                </h3>
                <div className="space-y-6" data-id="element-2134">
                  <div data-id="element-2135">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-2136">Story</h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-2137">
                      {coreTeam[selectedMember.index].story}
                    </p>
                  </div>
                  <div data-id="element-2138">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-2139">
                      Background
                    </h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-2140">
                      {coreTeam[selectedMember.index].background}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Split Screen Section Component
function SplitScreenSection({
  member,
  index,
  imageOnLeft,
  onReadMore
}: {
  member: TeamMember;
  index: number;
  imageOnLeft: boolean;
  onReadMore: () => void;
}) {
  const {
    scrollYProgress
  } = useScroll();
  const imageY = useTransform(scrollYProgress, [index * 0.25, (index + 1) * 0.25], ['20%', '-20%']);
  return <section className="min-h-screen flex items-center relative overflow-hidden bg-black" data-id="element-2141">
      <div className="w-full" data-id="element-2142">
        <div className="grid lg:grid-cols-2 lg:min-h-screen" data-id="element-2143">
          {/* Image Side */}
          <motion.div className={`relative min-h-[50vh] lg:min-h-0 ${imageOnLeft ? 'lg:order-1' : 'lg:order-2'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? -50 : 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2144">
            <motion.div className="absolute inset-0" style={{
            y: imageY
          }} data-id="element-2145">
              <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-black/50 to-transparent z-10" data-id="element-2146" />
              <img src={member.image} alt={member.name} className="w-full h-full object-cover" style={{
              objectPosition: 'center 20%'
            }} data-id="element-2147" />
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div className={`flex items-center px-6 sm:px-8 md:px-16 py-12 lg:py-20 ${imageOnLeft ? 'lg:order-2' : 'lg:order-1'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? 50 : -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-2148">
            <div data-id="element-2149">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4
            }} className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-2150">
                {member.role}
              </motion.p>

              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5
            }} className="text-5xl md:text-6xl font-bold text-white mb-8" data-id="element-2151">
                {member.name}
              </motion.h2>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.6
            }} className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8" data-id="element-2152">
                {member.story}
              </motion.p>

              <motion.button initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.7
            }} onClick={onReadMore} className="inline-flex items-center gap-2 px-6 py-3 min-h-[44px] bg-vitalityTeal/10 hover:bg-vitalityTeal/20 border border-vitalityTeal/30 rounded-lg text-vitalityTeal font-semibold transition-colors" data-id="element-2153">
                Read Full Background
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}